import "../../assets/css/requests/massiveStatusUpdateModal.css"
import close from "../../assets/images/close.svg"
import client from "../../assets/images/clients.svg"

import Modal from "react-bootstrap/Modal";
import Swal from 'sweetalert2'
import Axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/auth-user.jsx"

const {
    REACT_APP_URI_SUPPLIERS_NEW_REQUEST: URI_SUPPLIER_NEW_REQUEST,
    REACT_APP_URI_SUPPLIERS_PRE_AUTH: URI_SUPPLIER_PRE_AUTH,
    REACT_APP_URI_SUPPLIERS_MASSIVE_UPDATE: URI_SUPPLIER_MASSIVE_UPDATE
} = process.env

export default function MassiveStatusUpdateModal(props) {
    const { 
        show,
        handleClose
    } = props

    const { user } = useAuth()
    const userLogged = user?.selectedProfile

    const rolAbleTo = ["Master", "Super admin"]

    const [suppliers, setSuppliers] = useState([])
    const [loadingData, setLoadingData] = useState(false)
    
    const [tab, setTab] = useState(1)

    const [search, setSearch] = useState({
        newRequest: "",
        preAuthorized: ""
    })

    const [filter, setFilter] = useState({
        open: false,
        active: [],
        tempSelectedOptionsNewRequest: [],
        tempSelectedOptionsPreAuthorized: [],
        selectedOptionsNewRequest: [],
        selectedOptionsPreAuthorized: [],
    })

    const [selectedSuppliers, setSelectedSuppliers] = useState({
        newRequest: [],
        preAuthorized: []
    })

    useEffect(() => {
        document.body.addEventListener("click", closeFilters)

        return () => {
            document.body.removeEventListener("click", closeFilters)
        }
    }, [])

    const getSuppliers = useCallback(async ({ userLogged, signal }) => {
        try {
            const query = new URLSearchParams({
                dataID: userLogged?.dataID,
                rol: userLogged?.rol,
            })

            setLoadingData(true)

            const { data } = await Axios.get(`${URI_SUPPLIER_MASSIVE_UPDATE}?${query.toString()}`, { signal })

            setSuppliers(data?.suppliers ?? [])
        } catch (error) {
            if (error.name === "AbortError") return
            
            const errorMessage = error.response?.data?.message || "An error occurred while fetching suppliers.";
			console.error("Error fetching suppliers: ", errorMessage);
        } finally {
            setLoadingData(false)
        }
    }, [])

    useEffect(() => {
        if (!show || !userLogged) return
        
        const controller = new AbortController();

        const { signal } = controller;

        getSuppliers({ userLogged, signal })
    }, [show, userLogged, getSuppliers])
    
    useEffect(() => {
        if (!show) {
            setTab(1)
            setSearch({
                newRequest: "",
                preAuthorized: ""
            })
            setFilter({
                open: false,
                active: [],
                tempSelectedOptionsNewRequest: [],
                tempSelectedOptionsPreAuthorized: [],
                selectedOptionsNewRequest: [],
                selectedOptionsPreAuthorized: [],
            })
            setSelectedSuppliers({
                newRequest: [],
                preAuthorized: []
            })
        }
    }, [show])

    const closeFilters = (e) => {
        if (!e.target.classList.contains("filter") && !e.target.closest(".filterOptions")) {
            setFilter(prev => {
                return {
                    ...prev,
                    open: false,
                }
            })
        }
    }

    const handleSearch = (e) => {
        const { value } = e.target

        setSearch(prev => {
            return {
                ...prev,
                [tab === 1 ? "newRequest" : "preAuthorized"]: value
            }
        })
    }

    const handleTab = (tab) => {
        setTab(tab)
    }

    const handleOpenFilter = () => {
        if (loadingData) return
        
        setFilter(prev => {
            return {
                ...prev,
                open: !prev.open,
            }
        })
    }

    const handleFilterOptions = (e) => {
        const { value } = e.target

        setFilter(prev => {
            const key = tab === 1 ? "tempSelectedOptionsNewRequest" : "tempSelectedOptionsPreAuthorized"
            const selectedOptions = prev[key]?.includes(value) ? prev[key].filter(option => option !== value) : [...prev[key], value]

            return {
                ...prev,
                [key]: selectedOptions,
            }
        })
    }

    const clearFilterOptions = () => {
        setFilter(prev => {
            const clearActive = prev.active.filter(active => active !== tab)
            const key = tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized"
            const key2 = tab === 1 ? "tempSelectedOptionsNewRequest" : "tempSelectedOptionsPreAuthorized"
            
            return {
                ...prev,
                open: false,
                active: clearActive,
                [key]: [],
                [key2]: [],
            }
        })
    }

    const filterData = () => {
        setFilter(prev => {
            const isActive = prev.active.includes(tab) ? prev.active : [...prev.active, tab]
            const key = tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized"
            const key2 = tab === 1 ? "tempSelectedOptionsNewRequest" : "tempSelectedOptionsPreAuthorized"
            
            return {
                ...prev,
                open: false,
                active: isActive,
                [key]: prev[key2],
            }
        })
    }

    const handleSelectSupplier = (supplier, id) => {
        const selectedSupplier = {
            id,
            supplier
        }
        
        setSelectedSuppliers(prev => {
            const key = tab === 1 ? "newRequest" : "preAuthorized";

            const updatedArray = prev[key].some(item => item.id === id)
                ? prev[key].filter(item => item.id !== id)
                : [...prev[key], selectedSupplier];

            return {
                ...prev,
                [key]: updatedArray,
            };
        })
    }

    const searchFilter = useCallback((supplier) => {
        const searchValue = tab === 1 ? search.newRequest.toLocaleLowerCase() : search.preAuthorized.toLocaleLowerCase()

        const checkSearchValue = supplier?.companyName?.toLocaleLowerCase()?.includes(searchValue)

        return (!searchValue || checkSearchValue) && (supplier?.clients.some(c => c.status === (tab === 1 ? "New request" : "Pre-authorized")))
    }, [search, tab]);

    const handleSelectAll = () => {
        const key = tab === 1 ? "newRequest" : "preAuthorized";
        const selectedOptionsKey = tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized";

        const allVisibleSuppliers = suppliers
            ?.filter(supplier => searchFilter(supplier))
            ?.filter(supplier => filter.active.includes(tab) && 
                supplier?.clients?.some(c => c.status === (tab === 1 ? "New request" : "Pre-authorized") && 
                filter?.[selectedOptionsKey]?.includes(c.entityID?.toString())
            ))

        const allVisibleCount = allVisibleSuppliers?.length;

        if (allVisibleCount === 0) return setSearch(prev => {
            return {
                ...prev,
                [key]: ""
            }
        })

        setSelectedSuppliers(prev => {
            const isAllSelected = prev[key].length === allVisibleCount;
            
            return {
            ...prev,
            [key]: isAllSelected ? [] : allVisibleSuppliers.map(supplier => ({
                id: `${supplier.supplierID}-${key}`,
                supplier
            }))
            };
        });
    }

    const handleUpdateSuppliers = () => {
        const suppliersToChange = {
            supplierIDs: selectedSuppliers[tab === 1 ? "newRequest" : "preAuthorized"].map(item => item.supplier.supplierID),
            clientIDs: filter?.[tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized"] || [],
        } 

        const route = tab === 1 ? URI_SUPPLIER_NEW_REQUEST : URI_SUPPLIER_PRE_AUTH

        const dataToSend = {
            username: tab === 1 ? userLogged?.username : undefined,
            [tab === 1 ? "suppliersNewRequest" : "suppliersPreAuth"]: suppliersToChange,
        };

        Swal.fire({
            title: `Are you sure you want to update (${suppliersToChange.supplierIDs?.length ?? 0}) suppliers selected?`,
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: "Yes, update",
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    footer: 'Loading, please wait',
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                        Swal.showLoading()
                    },
                })

                Axios.patch(route, dataToSend).then(async res => {

                    await Swal.fire({
                        icon: 'success',
                        text: 'Information submitted successfully',
                        showConfirmButton: false,
                        timer: 5000,
                        timerProgressBar: true,
                    })

                    handleClose()

                }).catch(err => {
                    Swal.fire({
                        icon: 'error',
                        text: 'Failed to save information',
                        showConfirmButton: false,
                        timer: 5000,
                        timerProgressBar: true,
                    })
                    console.log("Error al actualizar los suppliers" + err)
                })
            }
        });
    }

    const clientsFilter = useMemo(() => {
        const getUniqueClients = (status) => {
            // Get unique clients by _id for the given status
            const clients = suppliers.reduce((acc, supplier) => {
                supplier.clients
                    ?.filter(c => c.status === status)
                    ?.forEach(c => {
                        acc[c.entityID] = {
                            _id: c.entityID,
                            entityName: c.clientName,
                            entityID: c.entityID
                        };
                    });
                return acc;
            }, {});

            return Object.values(clients);
        }

        return {
            newRequest: getUniqueClients("New request"),
            preAuthorized: getUniqueClients("Pre-authorized"),
        };
    }, [suppliers]);

    const confirmButtonTitle = useMemo(() => {
        const numberOfSelected = selectedSuppliers[tab === 1 ? "newRequest" : "preAuthorized"]?.length
        
        return tab === 1 ? `Send to Pre-authorized (${numberOfSelected})` : `Send to Authorized (${numberOfSelected})`
    }, [tab, selectedSuppliers])

    const isConfirmButtonDisabled = useMemo(() => {
        const numberOfSelected = selectedSuppliers[tab === 1 ? "newRequest" : "preAuthorized"]?.length

        return numberOfSelected === 0
    }, [tab, selectedSuppliers])

    const selectionButtonTitle = useMemo(() => {
        const allVisible = suppliers?.filter(supplier => searchFilter(supplier))
        ?.filter(supplier => filter.active.includes(tab) || 
            (selectedSuppliers?.[tab === 1 ? "newRequest" : "preAuthorized"]?.some(item => item.supplier.supplierID === supplier.supplierID
            && supplier.clients.some(c => filter?.[tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized"]?.includes(c.entityID)))
        ))
        ?.length

        return allVisible === 0 ? "Clear search" : selectedSuppliers?.[tab === 1 ? "newRequest" : "preAuthorized"]?.length === allVisible ? `Deselect all (${allVisible})` : `Select all (${allVisible})`
    }, [suppliers, filter, selectedSuppliers, tab, searchFilter])

    return (
        <Modal
            className={`modalMassiveStatusUpdate ${userLogged?.darkLightMode ? "darkMode" : ""}`}
            show={show}
            size="xl"
            centered
            onHide={handleClose}
            backdrop="static" // Prevents closing when clicking outside the modal
        >
            <Modal.Body>
                <img className="closeModal" src={close} alt="Prodensa Supplier" onClick={handleClose} />

                <h4>Supplier requests</h4>

                <div className="modalHeader">
                    <div className="modalTabs">
                        <h5 className={`${tab === 1 ? "active" : ""}`} onClick={() => handleTab(1)}>New request</h5>

                        {rolAbleTo.includes(userLogged?.rol) && <h5 className={`${tab === 2 ? "active" : ""}`} onClick={() => handleTab(2)}>Pre-Authorized</h5>}
                    </div>

                    <div className="filterOptionsContainer">
                        <div className={`filter ${filter.open ? "show" : null} ${filter.active?.includes(tab) ? "active" : undefined} ${loadingData ? "loading" : ""}`} onClick={handleOpenFilter}>
                            <img src={client} alt="Prodensa Supplier" />

                            <span>Client</span>
                            
                            <div className={`filterOptions ${filter.open ? "show" : null}`} onClick={(e) => e.stopPropagation()}>
                                <span className="title" onClick={(e) => e.stopPropagation()}>
                                    Client
                                </span>

                                <div className="optionsScroll" onClick={(e) => e.stopPropagation()}>
                                    {clientsFilter?.[tab === 1 ? "newRequest" : "preAuthorized"]?.map(entity => {
                                        const isChecked = filter?.[tab === 1 ? "tempSelectedOptionsNewRequest" : "tempSelectedOptionsPreAuthorized"]?.includes(entity._id?.toString())
                                        
                                        return (
                                            <div key={`clientFilter-${entity._id}`} className="option">
                                                <input 
                                                    checked={isChecked} 
                                                    className="entityCheck" 
                                                    value={entity._id} 
                                                    type="checkbox" 
                                                    id={`entity-${entity._id}`} 
                                                    onChange={handleFilterOptions} 
                                                />
                                                
                                                <label htmlFor={`entity-${entity._id}`} >{entity.entityName}</label>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className="actions">
                                    <button onClick={clearFilterOptions}>Clear</button>
                                    <button onClick={filterData}>Apply</button>
                                </div>
                            </div>
                        </div>

                        <button className="btnBgBlueTextGradient selectionButton" onClick={handleSelectAll}>{selectionButtonTitle}</button>
                    </div>

                    <div className="searchContainer">
                        <input type="text" placeholder="Search..." value={tab === 1 ? search.newRequest : search.preAuthorized} onChange={handleSearch} />
                    </div>
                </div>

                <div className={`suppliersTable ${loadingData ? "loading" : ""} ${!filter?.[tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized"]?.length > 0 && "showMessage"}`}>
                    {suppliers?.filter(supplier => searchFilter(supplier))
                    ?.filter(supplier => filter.active.includes(tab) && supplier?.clients?.some(c => c.status === (tab === 1 ? "New request" : "Pre-authorized") && filter?.[tab === 1 ? "selectedOptionsNewRequest" : "selectedOptionsPreAuthorized"]?.includes(c.entityID?.toString()))) 
                    ?.map(supplier => {
                        const id = `${supplier.supplierID}-${tab === 1 ? "newRequest" : "preAuthorized"}`

                        const isChecked = selectedSuppliers?.[tab === 1 ? "newRequest" : "preAuthorized"]?.some(item => item.id === id)

                        return (
                            <div key={id} className="supplierRow">
                                <input 
                                    type="checkbox" 
                                    id={id} 
                                    checked={isChecked} 
                                    onChange={() => handleSelectSupplier(supplier, id)} />

                                <label htmlFor={id}>
                                    {supplier.companyName}
                                </label>
                            </div>
                        )
                    })}
                </div>

                <div className="modalActions">
                    <button className="btnBgTextRed cancel" onClick={handleClose}>Cancel</button>

                    <button className="btnBgBlueTextGradient accept" onClick={handleUpdateSuppliers} disabled={isConfirmButtonDisabled}>
                        {confirmButtonTitle}
                    </button>
                </div>
			</Modal.Body>
		</Modal >
	)
}
